import axios from "axios";

/**
 * wa-scheduler#121 — attachment uploads must always settle.
 *
 * The composer clears its spinner AFTER the try/catch around the upload, so a
 * thrown error clears it fine; only a promise that never settles pins it
 * forever. The original implementation was a bare `axios PUT` with no timeout
 * (axios defaults to `0` = wait indefinitely), so a stalled connection to the
 * GCS signed URL hung the promise permanently — an eternal spinner, no error,
 * and no server-side trace because the request never completed.
 *
 * The bound is a STALL watchdog rather than a flat total timeout. The composer
 * accepts files up to WhatsApp's 16 MB cap, and a flat timeout would false-fail
 * a large upload that is slowly but genuinely progressing. The watchdog re-arms
 * on every upload-progress event and therefore fires only when bytes actually
 * stop moving. After the body is fully sent it keeps ticking from the last
 * progress event, so it also bounds a server that accepts the upload and then
 * never responds.
 */

/** No progress for this long ⇒ the connection is considered dead. */
const DEFAULT_STALL_TIMEOUT_MS = 30_000;
/** Initial + this many retries. A stall is usually transient and clears on a retry. */
const DEFAULT_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 500;

export class UploadStalledError extends Error {
    constructor(stallTimeoutMs: number) {
        super(`Upload stalled: no progress for ${stallTimeoutMs}ms`);
        this.name = "UploadStalledError";
    }
}

export type UploadFilePutOptions = {
    /** Abort after this long with no upload progress. Default 30s. */
    stallTimeoutMs?: number;
    /** Retries after the initial attempt. Default 2. */
    retries?: number;
    /** Caller-owned cancellation (e.g. the user removed the attachment). */
    signal?: AbortSignal;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Retry only what a retry can plausibly fix: stalls and transport errors, plus
 * 429/5xx. A 4xx from GCS means the signed URL is expired or was signed for a
 * different content type — retrying the same URL cannot fix either, so it fails
 * fast and the caller must request a fresh URL.
 */
function isRetryable(error: unknown): boolean {
    if (error instanceof UploadStalledError) return true;

    const status = (error as any)?.response?.status;
    if (status === undefined) return true; // no response at all ⇒ transport-level
    return status === 429 || status >= 500;
}

async function putOnce(
    file: File,
    url: string,
    stallTimeoutMs: number,
    externalSignal?: AbortSignal,
): Promise<void> {
    const controller = new AbortController();
    let stalled = false;
    let watchdog: ReturnType<typeof setTimeout> | undefined;

    const armWatchdog = () => {
        if (watchdog !== undefined) clearTimeout(watchdog);
        watchdog = setTimeout(() => {
            stalled = true;
            controller.abort();
        }, stallTimeoutMs);
    };

    const onExternalAbort = () => controller.abort();
    externalSignal?.addEventListener("abort", onExternalAbort);
    if (externalSignal?.aborted) controller.abort();

    armWatchdog();
    try {
        await axios({
            method: "PUT",
            url,
            data: file,
            headers: { "Content-Type": file.type },
            signal: controller.signal,
            // Re-arms the watchdog: progress proves the connection is alive.
            onUploadProgress: armWatchdog,
        });
    } catch (error) {
        // axios reports an abort as a generic cancellation; only the watchdog
        // knows it was a stall, so translate it into something actionable.
        if (stalled) throw new UploadStalledError(stallTimeoutMs);
        throw error;
    } finally {
        if (watchdog !== undefined) clearTimeout(watchdog);
        externalSignal?.removeEventListener("abort", onExternalAbort);
    }
}

export async function uploadFilePut(
    file: File,
    url: string,
    options: UploadFilePutOptions = {},
): Promise<void> {
    const stallTimeoutMs = options.stallTimeoutMs ?? DEFAULT_STALL_TIMEOUT_MS;
    const retries = options.retries ?? DEFAULT_RETRIES;

    let lastError: unknown;
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            await putOnce(file, url, stallTimeoutMs, options.signal);
            return;
        } catch (error) {
            lastError = error;
            // The caller cancelled deliberately — never retry over that.
            if (options.signal?.aborted) throw error;
            if (!isRetryable(error)) throw error;
            if (attempt < retries) await delay(RETRY_BASE_DELAY_MS * 2 ** attempt);
        }
    }
    throw lastError;
}
